import { useNavigate } from '@solidjs/router'
import { userStore } from '/web/store'

const steps = [
  {
    n: '01',
    title: 'Register For Free',
    text: "Sign up for free in under 30 seconds — you will not be charged. As soon as you create your account, you'll instantly receive bonus tokens to start exploring everything CosplayFans has to offer. Dive into live 24/7 chats with your favorite characters, generate stunning custom AI images on demand, and unlock exclusive VIP content from top creators. Your fantasy universe is just one click away!",
  },
  {
    n: '02',
    title: 'Choose Your Character',
    text: "Browse over 1,000 characters from anime, superheroes, villains, and hentai universes — each with their own unique personality, backstory, and visual style. Whether you're into fierce warriors, mysterious anti-heroes, or playful cosplay creators, there's someone waiting for you. Filter by genre, personality, or popularity to find your perfect match and start interacting instantly.",
  },
  {
    n: '03',
    title: 'Generate & Share',
    text: 'Use your tokens to generate stunning, one-of-a-kind AI images of your favorite characters — fully customized with the pose, outfit, scene, and style you want. Download high-resolution images instantly and share your creations on social media, with friends, or keep them in your private collection. Every image is uniquely yours, generated in seconds by our advanced AI engine. The possibilities are endless.',
  },
]

const perks = ['Live 24/7 Chat', 'AI Image Generation', '1,000+ Characters', 'Exclusive Content']

export default function StepsSection() {
  const state = userStore()
  const claimed = () => !!state.profile?.hasClaimedInitialTokens
  const loggedIn = () => state.loggedIn
  const navigate = useNavigate()

  return (
    <section class="cf-section" style={{ 'border-top': '1px solid var(--cf-hairline)' }}>
      <div class="grid grid-cols-1 items-start gap-7 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-10">
        {/* Left — Steps */}
        <div class="min-w-0">
          <div class="mb-6 text-left">
            <div class="cf-eyebrow pill mb-4 inline-flex">The Process</div>
            <h2 class="cf-h2">
              Three Steps to{' '}
              <em class="not-italic text-[var(--cf-cyan)]" style={{ 'font-style': 'italic' }}>
                Fantasy
              </em>
            </h2>
          </div>

          <div class="flex flex-col gap-7">
            {steps.map((s, i) => (
              <div
                class="flex gap-4 pb-6"
                classList={{ 'border-b border-[rgba(255,255,255,0.06)]': i < steps.length - 1 }}
              >
                {/* Keep our larger step numbers */}
                <div class="min-w-[36px] font-rajdhani text-[40px] font-black leading-none tracking-wide text-[var(--cf-accent)] lg:text-[48px]">
                  {s.n}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="mb-2 font-rajdhani text-[14px] font-black uppercase tracking-[0.5px] text-white lg:text-[17px]">
                    {s.title}
                  </div>
                  <div class="text-[13.5px] leading-[1.7] text-[var(--cf-muted)]">{s.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Bonus card */}
        <aside
          class="rounded-[20px] px-8 pb-7 pt-9 text-center lg:sticky"
          style={{
            top: 'calc(var(--cf-topbar-h) + 20px)',
            border: '1.5px solid rgba(138,43,226,0.45)',
            background:
              'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(138,43,226,0.18), transparent 70%), radial-gradient(ellipse 60% 50% at 100% 100%, rgba(0,229,255,0.10), transparent 70%), linear-gradient(180deg, #14101f 0%, #0c0916 100%)',
            'box-shadow': '0 0 40px rgba(138,43,226,0.15), 0 24px 60px rgba(0,0,0,0.5)',
          }}
        >
          {/* Eyebrow */}
          <div class="mb-4 inline-flex items-center gap-2 font-rajdhani text-[11px] font-bold uppercase tracking-[1.8px] text-[#FF3CAC]">
            <span class="text-xs leading-none">✦</span>
            {claimed() ? "You're All Set" : 'Limited Time Offer'}
          </div>

          {/* Headline */}
          <h3
            class="mb-4 font-bebas tracking-[1.5px] text-white"
            style={{ 'font-size': 'clamp(36px, 4vw, 56px)', 'line-height': '1' }}
          >
            {claimed() ? (
              <>
                YOUR TOKENS
                <br />
                ARE{' '}
                <span
                  class="cf-grad-text"
                  style={{ filter: 'drop-shadow(0 0 20px rgba(0,229,255,0.35))' }}
                >
                  READY!
                </span>
              </>
            ) : (
              <>
                GET{' '}
                <span
                  class="cf-grad-text"
                  style={{ filter: 'drop-shadow(0 0 20px rgba(0,229,255,0.35))' }}
                >
                  FREE
                </span>{' '}
                BONUS
                <br />
                TOKENS!
              </>
            )}
          </h3>

          {/* Sub */}
          <p class="mx-auto mb-5 max-w-[380px] text-[14px] leading-[1.65] text-[rgba(255,255,255,0.55)]">
            {claimed()
              ? 'Your free tokens are waiting in your account. Dive into live chats, generate AI images, and explore exclusive content — start now.'
              : 'Create your account and instantly unlock tokens to access live chats, exclusive content, and AI image generation — completely free to start.'}
          </p>

          {/* Perks grid */}
          <div class="mb-6 grid grid-cols-2 gap-2.5">
            {perks.map((p) => (
              <div class="inline-flex items-center gap-2 rounded-full border border-[rgba(138,43,226,0.3)] bg-[rgba(138,43,226,0.10)] px-3.5 py-2.5 font-rajdhani text-[12px] font-bold tracking-[0.3px] text-[rgba(255,255,255,0.78)]">
                <span class="text-[11px] leading-none text-[#8A2BE2]">✦</span>
                {p}
              </div>
            ))}
          </div>

          {/* Gift card */}
          <div class="mb-5 flex items-center gap-3.5 rounded-[14px] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] p-4 text-left">
            <div
              class="grid h-[54px] w-[54px] flex-shrink-0 place-items-center rounded-full text-[26px]"
              style={{
                background: 'rgba(0,229,255,0.08)',
                border: '1.5px solid rgba(0,229,255,0.35)',
                'box-shadow': '0 0 18px rgba(0,229,255,0.25)',
                animation: 'bonusGiftPulse 2.4s ease-in-out infinite',
              }}
            >
              {claimed() ? '✨' : '🎁'}
            </div>
            <div class="min-w-0 flex-1">
              <div class="font-rajdhani text-[11px] font-bold uppercase tracking-[1.8px] text-[#FF3CAC]">
                {claimed() ? 'Tokens Added' : 'Free Bonus'}
              </div>
              <div class="font-bebas text-[22px] tracking-[1px] text-white" style={{ 'line-height': '1.1' }}>
                {claimed() ? 'ENJOY!' : 'TOKENS!'}
              </div>
            </div>
            <div class="rounded-lg border border-[rgba(138,43,226,0.4)] bg-[rgba(138,43,226,0.18)] px-3 py-1.5 font-rajdhani text-[12px] font-extrabold tracking-[1px] text-[#00E5FF]">
              {claimed() ? '✓' : 'FREE'}
            </div>
          </div>

          {/* CTA */}
          <button
            class="mb-4 w-full rounded-full py-[18px] font-rajdhani text-[15px] font-extrabold uppercase tracking-[1px] text-white transition-all hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, #8A2BE2, #FF3CAC)',
              'box-shadow':
                '0 0 24px rgba(255,60,172,0.35), 0 10px 30px rgba(138,43,226,0.35)',
            }}
            onClick={() => {
              if (claimed()) navigate('/generate-image/')
              else if (loggedIn()) userStore.buyTokensModal(true)
              else userStore.loginModal(true)
            }}
          >
            {claimed() ? 'Start Creating →' : 'Claim My Free Tokens →'}
          </button>

          {/* Fine print */}
          <div class="flex flex-wrap justify-center gap-3 text-[11px] text-[rgba(255,255,255,0.35)]">
            <span>🔒 Secure / Discreet</span>
            <span>{claimed() ? '✦ Tokens Ready' : '✦ No Charge Today'}</span>
            <span>{claimed() ? '✦ New Drops Weekly' : '✦ No Commitment'}</span>
          </div>
        </aside>
      </div>

      <style>{`
        @keyframes bonusGiftPulse {
          0%, 100% { box-shadow: 0 0 18px rgba(0,229,255,0.2); }
          50% { box-shadow: 0 0 28px rgba(0,229,255,0.5); }
        }
      `}</style>
    </section>
  )
}
